// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState, type Dispatch, type ReactNode } from 'react';

import { api } from '../../../api/client';
import type { SolidSpace } from '../../../api/types';
import { useAuth } from '../../../app/AuthGate';
import type { Storey } from '../../../bygning/storeys';
import {
  EMPTY_FACTS,
  mergeFacts,
  settingsReducer,
  type GroundFacts,
  type ModelSettingsState,
  type SettingsAction,
} from './settingsState';
import { browserStorage, loadSettings, saveSettings, type WrittenCache } from './settingsStorage';

interface StateValue {
  state: ModelSettingsState;
  dispatch: Dispatch<SettingsAction>;
}

const NO_STOREYS: Storey[] = [];
const NO_SPACES: SolidSpace[] | null = null;

const StateContext = createContext<StateValue | null>(null);
const DispatchContext = createContext<Dispatch<SettingsAction> | null>(null);
const FactsContext = createContext<GroundFacts | null>(null);
const PublishContext = createContext<((patch: Partial<GroundFacts>) => void) | null>(null);
const StoreysContext = createContext<Storey[]>(NO_STOREYS);
const SpacesContext = createContext<SolidSpace[] | null>(NO_SPACES);

/**
 * Where the Ground and the gear panel meet. The Ground and the left tile are
 * siblings in the shell, so the state cannot live in either: the shell wraps
 * both in this provider (stage 2), the Ground reads the settings and
 * publishes what it learns (`GroundFacts`), and `ModelSettings` reads both.
 *
 * Three contexts, so the Ground — which publishes facts — does not re-render
 * on its own publications: it subscribes to the settings and the publisher
 * only; the panel subscribes to the facts as well. A fourth carries just the
 * derived storeys (gap 7), for the shell's breadcrumb, the palette and
 * Overblik: they re-render when the storeys change, not on every fact. A
 * fifth, likewise, carries the building model's spaces (`GET /spaces`,
 * frontend-2ay) for Overblik's room count/list.
 */
export function SettingsProvider({ children }: { children: ReactNode }) {
  // Restored before the first render (no flash of defaults): the global
  // preferences, the case's choices and this page load's (`settingsStorage.ts`,
  // frontend-38f), so leaving for an expert page and coming back keeps them.
  // Keyed by the signed-in user (null outside an AuthGate: a share link).
  const user = useAuth()?.me.user.id ?? null;
  const [state, dispatch] = useReducer(settingsReducer, api.caseId, (cid) => loadSettings(browserStorage(), user, cid));
  const written = useRef<WrittenCache>(new Map());
  useEffect(() => {
    saveSettings(browserStorage(), user, api.caseId, state, written.current);
  }, [user, state]);
  const [facts, setFacts] = useState<GroundFacts>(EMPTY_FACTS);
  const publish = useCallback((patch: Partial<GroundFacts>) => setFacts((prev) => mergeFacts(prev, patch)), []);
  const value = useMemo(() => ({ state, dispatch }), [state]);
  return (
    <DispatchContext.Provider value={dispatch}>
      <StateContext.Provider value={value}>
        <PublishContext.Provider value={publish}>
          <FactsContext.Provider value={facts}>
            {/* `mergeFacts` keeps the array's identity while the storeys (or spaces) are unchanged. */}
            <StoreysContext.Provider value={facts.storeys}>
              <SpacesContext.Provider value={facts.spaces}>{children}</SpacesContext.Provider>
            </StoreysContext.Provider>
          </FactsContext.Provider>
        </PublishContext.Provider>
      </StateContext.Provider>
    </DispatchContext.Provider>
  );
}

/**
 * The storeys the Ground derived (`GroundFacts.storeys`), without
 * subscribing to the other facts. Empty outside a provider, before the
 * cloud has streamed, and for a single-storey building.
 */
export function useStoreys(): Storey[] {
  return useContext(StoreysContext);
}

/**
 * The building model's spaces (`GET /spaces`, `GroundFacts.spaces`), without
 * subscribing to the other facts. Null outside a provider, before the Ground
 * has read the route, or on a server without it.
 */
export function useSpaces(): SolidSpace[] | null {
  return useContext(SpacesContext);
}

/**
 * The settings' dispatch alone (stable), for the shell's palette commands:
 * no re-render on a settings change. Null outside a provider.
 */
export function useSettingsDispatch(): Dispatch<SettingsAction> | null {
  return useContext(DispatchContext);
}

export interface SettingsValue extends StateValue {
  facts: GroundFacts;
  publish: (patch: Partial<GroundFacts>) => void;
}

/** Settings, facts and the publisher. Throws outside `SettingsProvider`. */
export function useSettings(): SettingsValue {
  const s = useContext(StateContext);
  const facts = useContext(FactsContext);
  const publish = useContext(PublishContext);
  if (!s || !facts || !publish) throw new Error('useSettings must be used inside <SettingsProvider>');
  return { ...s, facts, publish };
}

/**
 * The Ground's view: settings and the publisher, without subscribing to the
 * facts. Null outside a provider — the Ground then keeps its own copy of the
 * settings (and the gear panel, if any, cannot reach it).
 */
export function useGroundSettings(): (StateValue & { publish: (patch: Partial<GroundFacts>) => void }) | null {
  const s = useContext(StateContext);
  const publish = useContext(PublishContext);
  return s && publish ? { ...s, publish } : null;
}
